import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faNewspaper,
  faComment,
  faTrash,
  faSpinner,
  faSearch,
  faShieldAlt,
  faTimes,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import api from '../lib/api';
import { useToast } from '../components/common/Toast';
import { logger } from '../lib/logger';

const Admin = () => {
  const authUser = useSelector((state) => state.auth.user);
  const isAdmin = authUser?.is_admin || false;
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('users');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data states
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  
  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState({ open: false, type: '', item: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const data = await api.admin.getUsers();
      setUsers(data || []);
    } catch (error) {
      logger.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  }, [toast]);

  // Fetch posts
  const fetchPosts = useCallback(async () => {
    try {
      const data = await api.admin.getPosts();
      setPosts(data || []);
    } catch (error) {
      logger.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    }
  }, [toast]);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    try {
      const data = await api.admin.getComments();
      setComments(data || []);
    } catch (error) {
      logger.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    }
  }, [toast]);

  // Load data based on active tab
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      if (activeTab === 'users') {
        await fetchUsers();
      } else if (activeTab === 'posts') {
        await fetchPosts();
      } else if (activeTab === 'comments') {
        await fetchComments();
      }
      
      setIsLoading(false);
    };

    loadData();
  }, [activeTab, fetchUsers, fetchPosts, fetchComments]);

  // Delete user
  const deleteUser = async (userId) => {
    setIsDeleting(true);
    try {
      await api.admin.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      logger.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setIsDeleting(false);
      setDeleteModal({ open: false, type: '', item: null });
    }
  };

  // Delete post
  const deletePost = async (postId) => {
    setIsDeleting(true);
    try {
      await api.admin.deletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post deleted successfully');
    } catch (error) {
      logger.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    } finally {
      setIsDeleting(false);
      setDeleteModal({ open: false, type: '', item: null });
    }
  };

  // Delete comment
  const deleteComment = async (commentId) => {
    setIsDeleting(true);
    try {
      await api.admin.deleteComment(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted successfully');
    } catch (error) {
      logger.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    } finally {
      setIsDeleting(false);
      setDeleteModal({ open: false, type: '', item: null });
    }
  };

  // Handle delete confirmation
  const handleDelete = () => {
    if (deleteModal.type === 'user') {
      deleteUser(deleteModal.item.id);
    } else if (deleteModal.type === 'post') {
      deletePost(deleteModal.item.id);
    } else if (deleteModal.type === 'comment') {
      deleteComment(deleteModal.item.id);
    }
  };

  // Filter data based on search
  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPosts = posts.filter(p => 
    p.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.user?.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredComments = comments.filter(c => 
    c.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.user?.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'users', label: 'Users', icon: faUsers, count: users.length },
    { id: 'posts', label: 'Posts', icon: faNewspaper, count: posts.length },
    { id: 'comments', label: 'Comments', icon: faComment, count: comments.length }
  ];

  // Access denied screen for non-admins
  if (!isAdmin) {
    return (
      <div className="p-4 max-w-2xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="bg-white rounded-card border border-subtle-border p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-frost-blue flex items-center justify-center">
            <FontAwesomeIcon icon={faLock} className="w-8 h-8 text-cool-steel" />
          </div>
          <h1 className="text-h2 text-charcoal mb-2">Access Denied</h1>
          <p className="text-body text-misty-slate mb-6">
            You don't have permission to access the admin panel.
            <br />
            This area is restricted to administrators only.
          </p>
          <Link
            to="/feed"
            className="inline-block px-6 py-2 bg-cool-steel text-white rounded-card
              hover:opacity-80 transition-opacity duration-200 ease-in-out"
          >
            Return to Feed
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <FontAwesomeIcon icon={faShieldAlt} className="w-6 h-6 text-cool-steel" />
          <h1 className="text-h1 text-charcoal">Admin Panel</h1>
        </div>
        <p className="text-body text-misty-slate">Manage users, posts, and comments</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setSearchQuery('');
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-card transition-all duration-200 ease-in-out ${
              activeTab === tab.id
                ? 'bg-cool-steel text-white'
                : 'bg-white border border-subtle-border text-charcoal hover:border-cool-steel hover:bg-frost-blue'
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="w-4 h-4" />
            <span className="font-medium">{tab.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              activeTab === tab.id ? 'bg-white/20' : 'bg-frost-blue'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <FontAwesomeIcon 
            icon={faSearch} 
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-misty-slate" 
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${activeTab}...`}
            className="w-full pl-12 pr-4 py-3 border border-subtle-border rounded-card
              focus:outline-none focus:border-cool-steel focus:ring-2 focus:ring-frost-blue
              transition-all duration-200 ease-in-out"
          />
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-card border border-subtle-border">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-cool-steel animate-spin" />
          </div>
        ) : (
          <>
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="divide-y divide-subtle-border">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-12 text-misty-slate">
                    No users found
                  </div>
                ) : (
                  filteredUsers.map(user => (
                    <div key={user.id} className="p-4 flex items-center justify-between hover:bg-frost-blue/30 transition-colors duration-200">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-cool-steel flex items-center justify-center text-white font-semibold">
                          {user.display_name?.[0] || user.username?.[0] || '?'}
                        </div>
                        <div>
                          <div className="font-medium text-charcoal">
                            {user.display_name || user.username}
                          </div>
                          <div className="text-body-sm text-misty-slate">
                            @{user.username} · {user.email}
                          </div>
                          <div className="text-xs text-misty-slate mt-1">
                            Joined {formatDate(user.created_at)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setDeleteModal({ open: true, type: 'user', item: user })}
                        className="p-2 text-misty-slate hover:text-red-500 hover:bg-red-50 rounded-card transition-all duration-200"
                        title="Delete user"
                      >
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Posts Tab */}
            {activeTab === 'posts' && (
              <div className="divide-y divide-subtle-border">
                {filteredPosts.length === 0 ? (
                  <div className="text-center py-12 text-misty-slate">
                    No posts found
                  </div>
                ) : (
                  filteredPosts.map(post => (
                    <div key={post.id} className="p-4 flex items-start justify-between hover:bg-frost-blue/30 transition-colors duration-200">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-body-sm text-misty-slate">
                            by @{post.user?.username || 'unknown'}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-frost-blue rounded-full text-cool-steel">
                            {post.type}
                          </span>
                        </div>
                        {post.title && (
                          <div className="font-medium text-charcoal mb-1">
                            {post.title}
                          </div>
                        )}
                        <div className="text-body text-charcoal line-clamp-2">
                          {post.content || 'No content'}
                        </div>
                        <div className="text-xs text-misty-slate mt-2">
                          {formatDate(post.created_at)}
                        </div>
                      </div>
                      <button
                        onClick={() => setDeleteModal({ open: true, type: 'post', item: post })}
                        className="p-2 text-misty-slate hover:text-red-500 hover:bg-red-50 rounded-card transition-all duration-200 ml-4"
                        title="Delete post"
                      >
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Comments Tab */}
            {activeTab === 'comments' && (
              <div className="divide-y divide-subtle-border">
                {filteredComments.length === 0 ? (
                  <div className="text-center py-12 text-misty-slate">
                    No comments found
                  </div>
                ) : (
                  filteredComments.map(comment => (
                    <div key={comment.id} className="p-4 flex items-start justify-between hover:bg-frost-blue/30 transition-colors duration-200">
                      <div className="flex-1 min-w-0">
                        <div className="text-body-sm text-misty-slate mb-1">
                          @{comment.user?.username || 'unknown'} on "{comment.post?.title || comment.post?.content?.substring(0, 30) || 'post'}..."
                        </div>
                        <div className="text-body text-charcoal">
                          {comment.content}
                        </div>
                        <div className="text-xs text-misty-slate mt-2">
                          {formatDate(comment.created_at)}
                        </div>
                      </div>
                      <button
                        onClick={() => setDeleteModal({ open: true, type: 'comment', item: comment })}
                        className="p-2 text-misty-slate hover:text-red-500 hover:bg-red-50 rounded-card transition-all duration-200 ml-4"
                        title="Delete comment"
                      >
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteModal({ open: false, type: '', item: null })}
        >
          <div
            className="bg-white rounded-card p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-h3 text-charcoal">Confirm Delete</h3>
              <button
                onClick={() => setDeleteModal({ open: false, type: '', item: null })}
                className="p-2 text-misty-slate hover:text-charcoal transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-body text-misty-slate mb-6">
              Are you sure you want to delete this {deleteModal.type}? 
              {deleteModal.type === 'user' && (
                <span className="block mt-2 text-red-500 font-medium">
                  This will also delete all their posts, comments, and related data.
                </span>
              )}
            </p>

            <div className="bg-frost-blue rounded-card p-3 mb-6">
              {deleteModal.type === 'user' && (
                <div className="text-body-sm text-charcoal">
                  <strong>{deleteModal.item?.display_name || deleteModal.item?.username}</strong>
                  <br />
                  <span className="text-misty-slate">{deleteModal.item?.email}</span>
                </div>
              )}
              {deleteModal.type === 'post' && (
                <div className="text-body-sm text-charcoal line-clamp-2">
                  {deleteModal.item?.content || 'No content'}
                </div>
              )}
              {deleteModal.type === 'comment' && (
                <div className="text-body-sm text-charcoal line-clamp-2">
                  {deleteModal.item?.content}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, type: '', item: null })}
                className="flex-1 px-4 py-2 border border-subtle-border text-charcoal rounded-card
                  hover:border-cool-steel hover:bg-frost-blue transition-all duration-200 ease-in-out"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-card
                  hover:bg-red-600 transition-all duration-200 ease-in-out disabled:opacity-50"
              >
                {isDeleting ? (
                  <span className="flex items-center justify-center gap-2">
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    Deleting...
                  </span>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
